import { CircleAlert, CircleCheckBig, Info, Loader, Sparkles, TriangleAlert, X } from 'lucide-react';
import { forwardRef, type HTMLAttributes, type MouseEventHandler, type ReactNode } from 'react';
import { cn } from '../lib/cn';

export type BannerVariant = 'info' | 'warning' | 'error' | 'success' | 'offline' | 'ai';

export interface BannerAction {
  label: ReactNode;
  onClick?: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  /** Render as a link. */
  href?: string;
}

export interface BannerProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  variant?: BannerVariant;
  /** Bold lead-in ("Working offline."). */
  title?: ReactNode;
  /** Inline trailing action rendered as a link ("Change", "Ask AI", "Retry now"). */
  action?: BannerAction;
  /** Shows a × button. */
  onDismiss?: () => void;
  /** Replaces the variant icon (`null` hides it). */
  icon?: ReactNode;
  labels?: Partial<{ dismiss: string }>;
}

const VARIANTS: Record<BannerVariant, { box: string; icon: ReactNode; role?: 'status' | 'alert' }> = {
  info: {
    box: 'border border-transparent bg-info-soft text-info-text',
    icon: <Info strokeWidth={2.2} />,
  },
  warning: {
    box: 'border border-warning bg-warning-soft text-warning-text',
    icon: <TriangleAlert strokeWidth={2.2} />,
    role: 'status',
  },
  offline: {
    box: 'border border-warning bg-warning-soft text-warning-text',
    icon: <Loader strokeWidth={2.2} />,
    role: 'status',
  },
  error: {
    box: 'border border-error bg-error-soft text-error-text',
    icon: <CircleAlert strokeWidth={2.2} />,
    role: 'alert',
  },
  success: {
    box: 'border border-success bg-success-soft text-success-text',
    icon: <CircleCheckBig strokeWidth={2.2} />,
  },
  ai: {
    box: 'border border-dashed border-primary bg-primary-soft text-primary-text',
    icon: <Sparkles strokeWidth={2} />,
    role: 'status',
  },
};

/**
 * Inline page/section banner: info · warning · error · success · offline ("Working offline. 2 items
 * queued…") · ai (nudge with "Ask AI"). Optional action link and dismiss button.
 */
export const Banner = forwardRef<HTMLDivElement, BannerProps>(function Banner(
  { variant = 'info', title, action, onDismiss, icon, labels, className, children, role, ...rest },
  ref,
) {
  const v = VARIANTS[variant];
  const l = { dismiss: 'Dismiss', ...labels };
  const iconNode = icon === undefined ? v.icon : icon;
  return (
    <div
      ref={ref}
      role={role ?? v.role}
      className={cn('flex items-center gap-2.5 rounded-md px-3 py-2 text-13', v.box, className)}
      {...rest}
    >
      {iconNode ? (
        <span aria-hidden className="inline-flex shrink-0 [&>svg]:size-4">
          {iconNode}
        </span>
      ) : null}
      <span className="min-w-0 flex-1">
        {title ? <b>{title}</b> : null}
        {title && children ? ' ' : null}
        {children}
      </span>
      {action ? (
        action.href ? (
          <a
            href={action.href}
            onClick={action.onClick}
            className="shrink-0 font-medium text-inherit underline"
          >
            {action.label}
          </a>
        ) : (
          <button
            type="button"
            onClick={action.onClick}
            className="shrink-0 font-medium text-inherit underline"
          >
            {action.label}
          </button>
        )
      ) : null}
      {onDismiss ? (
        <button
          type="button"
          aria-label={l.dismiss}
          onClick={onDismiss}
          className="grid size-5 shrink-0 place-items-center rounded-sm opacity-60 transition-opacity hover:opacity-100"
        >
          <X aria-hidden className="size-3.5" strokeWidth={2} />
        </button>
      ) : null}
    </div>
  );
});
